import { useState } from 'react'
import { Sparkles } from 'lucide-react'
import type { ClientDraft, ContactDraft, ManualMode } from './types'
import { validateDraft } from './parse'

const VERTICALS = [
  'Dental',
  'Healthcare',
  'Veterinary',
  'Restaurant',
  'Home services',
  'Automotive',
  'Legal',
  'Real estate',
  'Fitness',
  'Retail',
  'Professional services',
]

interface ManualIntakeProps {
  mode: ManualMode
  onModeChange: (mode: ManualMode) => void
  onSubmit: (payload: { draft: ClientDraft; contact?: ContactDraft }) => void
  isSubmitting: boolean
}

export function ManualIntake({ mode, onModeChange, onSubmit, isSubmitting }: ManualIntakeProps) {
  const advanced = mode === 'advanced'

  // Core fields
  const [name, setName] = useState('')
  const [vertical, setVertical] = useState('')
  const [plan, setPlan] = useState('')
  const [health, setHealth] = useState<ClientDraft['health']>('good')
  const [mrr, setMrr] = useState('')

  // Advanced fields
  const [status, setStatus] = useState<ClientDraft['status']>('active')
  const [kind, setKind] = useState<ClientDraft['kind']>('client')
  const [website, setWebsite] = useState('')
  const [notes, setNotes] = useState('')
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [contactRole, setContactRole] = useState('')

  const [errors, setErrors] = useState<string[]>([])

  const buildDraft = (): ClientDraft => {
    const draft: ClientDraft = { name: name.trim() }
    if (vertical.trim()) draft.vertical = vertical.trim()
    if (plan.trim()) draft.plan = plan.trim()
    if (health) draft.health = health
    if (mrr.trim()) {
      const n = Number(mrr.replace(/[$,\s]/g, ''))
      draft.mrr = Number.isFinite(n) ? Math.round(n) : NaN
    }
    if (advanced) {
      if (status) draft.status = status
      if (kind) draft.kind = kind
      if (website.trim()) {
        const w = website.trim()
        draft.website = /^https?:\/\//i.test(w) ? w : `https://${w}`
      }
      if (notes.trim()) draft.notes = notes.trim()
    }
    return draft
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const draft = buildDraft()
    // NaN slips past the integer check as "not a whole number" — normalize the copy.
    const problems = validateDraft(draft).map((e) =>
      Number.isNaN(draft.mrr) && e.startsWith('MRR') ? 'MRR must be a whole number' : e,
    )
    if (problems.length) {
      setErrors(problems)
      return
    }
    setErrors([])

    let contact: ContactDraft | undefined
    if (advanced && contactName.trim()) {
      contact = { name: contactName.trim() }
      if (contactEmail.trim()) contact.email = contactEmail.trim()
      if (contactPhone.trim()) contact.phone = contactPhone.trim()
      if (contactRole.trim()) contact.role = contactRole.trim()
    }

    onSubmit({ draft, contact })
  }

  return (
    <form className="intake-manual" onSubmit={handleSubmit}>
      {/* Quick / Advanced toggle */}
      <div className="intake-mode-toggle" role="tablist" aria-label="Entry detail level">
        <button
          type="button"
          role="tab"
          aria-selected={!advanced}
          className={`intake-mode-btn${!advanced ? ' intake-mode-btn--active' : ''}`}
          onClick={() => onModeChange('quick')}
        >
          Quick
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={advanced}
          className={`intake-mode-btn${advanced ? ' intake-mode-btn--active' : ''}`}
          onClick={() => onModeChange('advanced')}
        >
          Advanced
        </button>
      </div>

      <div className="intake-field">
        <label htmlFor="intake-name">Client name</label>
        <input
          id="intake-name"
          autoFocus
          className="intake-input"
          placeholder="e.g. Harbor View Dentistry"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </div>

      <div className="intake-grid-2">
        <div className="intake-field">
          <label htmlFor="intake-vertical">Vertical</label>
          <input
            id="intake-vertical"
            className="intake-input"
            list="intake-verticals"
            placeholder="Dental"
            value={vertical}
            onChange={(e) => setVertical(e.target.value)}
          />
          <datalist id="intake-verticals">
            {VERTICALS.map((v) => (
              <option key={v} value={v} />
            ))}
          </datalist>
        </div>
        <div className="intake-field">
          <label htmlFor="intake-plan">Plan</label>
          <input
            id="intake-plan"
            className="intake-input"
            placeholder="Retainer + Answer"
            value={plan}
            onChange={(e) => setPlan(e.target.value)}
          />
        </div>
      </div>

      <div className="intake-grid-2">
        <div className="intake-field">
          <label htmlFor="intake-health">Health</label>
          <select
            id="intake-health"
            className="intake-input"
            value={health}
            onChange={(e) => setHealth(e.target.value as ClientDraft['health'])}
          >
            <option value="good">Healthy</option>
            <option value="watch">Watch</option>
            <option value="at-risk">At risk</option>
          </select>
        </div>
        <div className="intake-field">
          <label htmlFor="intake-mrr">
            MRR <span className="intake-optional">· optional</span>
          </label>
          <div className="intake-input-prefixed">
            <span className="intake-prefix mono">$</span>
            <input
              id="intake-mrr"
              className="intake-input"
              inputMode="numeric"
              placeholder="1200"
              value={mrr}
              onChange={(e) => setMrr(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Advanced-only section */}
      {advanced && (
        <div className="intake-advanced">
          <div className="intake-grid-2">
            <div className="intake-field">
              <label htmlFor="intake-status">Status</label>
              <select
                id="intake-status"
                className="intake-input"
                value={status}
                onChange={(e) => setStatus(e.target.value as ClientDraft['status'])}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="churned">Churned</option>
              </select>
            </div>
            <div className="intake-field">
              <label htmlFor="intake-kind">Kind</label>
              <select
                id="intake-kind"
                className="intake-input"
                value={kind}
                onChange={(e) => setKind(e.target.value as ClientDraft['kind'])}
              >
                <option value="client">Client</option>
                <option value="internal">Internal</option>
              </select>
            </div>
          </div>

          <div className="intake-grid-2">
            <div className="intake-field">
              <label htmlFor="intake-website">
                Website <span className="intake-optional">· optional</span>
              </label>
              <input
                id="intake-website"
                className="intake-input"
                placeholder="harborviewdental.com"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>
          </div>

          <div className="intake-field">
            <label htmlFor="intake-notes">
              Notes <span className="intake-optional">· optional</span>
            </label>
            <textarea
              id="intake-notes"
              className="intake-input intake-textarea"
              rows={2}
              placeholder="Context, how they came in, anything worth remembering."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <p className="intake-eyebrow">Primary contact · optional</p>
          <div className="intake-grid-2">
            <div className="intake-field">
              <label htmlFor="intake-contact-name">Name</label>
              <input
                id="intake-contact-name"
                className="intake-input"
                placeholder="Dr. Alex Rivera"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
              />
            </div>
            <div className="intake-field">
              <label htmlFor="intake-contact-role">Role</label>
              <input
                id="intake-contact-role"
                className="intake-input"
                placeholder="Owner"
                value={contactRole}
                onChange={(e) => setContactRole(e.target.value)}
              />
            </div>
          </div>
          <div className="intake-grid-2">
            <div className="intake-field">
              <label htmlFor="intake-contact-email">Email</label>
              <input
                id="intake-contact-email"
                className="intake-input"
                type="email"
                placeholder="name@business.com"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
              />
            </div>
            <div className="intake-field">
              <label htmlFor="intake-contact-phone">Phone</label>
              <input
                id="intake-contact-phone"
                className="intake-input"
                placeholder="(415) 555-0121"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {errors.length > 0 && (
        <ul className="intake-errors">
          {errors.map((err) => (
            <li key={err}>{err}</li>
          ))}
        </ul>
      )}

      <div className="intake-footer">
        <p className="intake-footer-note">
          {advanced ? (
            <>
              <Sparkles size={13} /> A primary contact is saved alongside the account.
            </>
          ) : (
            'Switch to Advanced for status, website, notes, and a contact.'
          )}
        </p>
        <button type="submit" className="btn btn-primary btn-sm" disabled={isSubmitting || !name.trim()}>
          {isSubmitting ? 'Adding…' : 'Add client'}
        </button>
      </div>
    </form>
  )
}
